/**
 * AnimeCard Component
 * Glassmorphic card displaying anime info
 */

import { useState, useRef, useCallback } from 'react'
import {
  Plus,
  ExternalLink,
  MoreHorizontal,
  Clock,
  Play,
  XCircle,
  SkipForward
} from 'lucide-react'
import { getPlatformName } from '../utils/parser'
import { calculateProgress, formatNextEpisode, isAiringNow } from '../utils/scheduler'

const formatSchedule = (broadcast) => {
  if (!broadcast || !broadcast.day) return null
  
  const dayMap = {
    'Sundays': '日曜日', 'Mondays': '月曜日', 'Tuesdays': '火曜日', 'Wednesdays': '水曜日',
    'Thursdays': '木曜日', 'Fridays': '金曜日', 'Saturdays': '土曜日'
  }
  
  const day = dayMap[broadcast.day] || broadcast.day
  let timeStr = ''

  if (broadcast.time) {
    const [hourStr, minStr] = broadcast.time.split(':')
    const hour = parseInt(hourStr, 10)
    
    if (!isNaN(hour)) {
      const isPM = hour >= 12
      const displayHour = hour > 12 ? hour - 12 : hour
      timeStr = `${isPM ? '午後' : '午前'}${displayHour}時`
      if (minStr && minStr !== '00') {
        timeStr += `${minStr}分`
      }
    } else {
      timeStr = broadcast.time
    }
  }
  
  return `毎週${day} ${timeStr}新着`
}

export function AnimeCard({
  anime,
  onIncrement,
  onEdit,
  onDrop,
  onSkip,
  onOpenUrl,
  onLongPress
}) {
  const [isAnimating, setIsAnimating] = useState(false)
  const [isLongPressing, setIsLongPressing] = useState(false)
  const longPressTimer = useRef(null)
  const isLongPressTriggered = useRef(false)

  // 長押し開始
  const handlePressStart = useCallback((e) => {
    isLongPressTriggered.current = false
    setIsLongPressing(true)

    longPressTimer.current = setTimeout(() => {
      isLongPressTriggered.current = true
      setIsLongPressing(false)

      // 長押し完了 - 確認ダイアログ
      if (window.confirm(`「${anime.title}」をゴミ箱へ移動しますか？`)) {
        onLongPress?.(anime.id)
      }
    }, 800) // 800ms長押し
  }, [anime.id, anime.title, onLongPress])

  // 長押し終了
  const handlePressEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
    setIsLongPressing(false)
  }, [])

  // 長押し中に移動したらキャンセル
  const handlePressCancel = useCallback(() => {
    handlePressEnd()
  }, [handlePressEnd])

  // クリック処理（長押し後はクリックをキャンセル）
  const handleClick = useCallback(() => {
    if (isLongPressTriggered.current) {
      isLongPressTriggered.current = false
      return
    }
    onEdit?.(anime)
  }, [anime, onEdit])

  const {
    title,
    currentEpisode = 0,
    totalEpisodes,
    platform,
    url,
    status,
    nextEpisodeDate,
    season: rawSeason = 'シーズン1',
    metadata
  } = anime

  // "Season X" を "シーズンX" に統一
  const season = rawSeason?.replace(/^Season\s*/i, 'シーズン') || 'シーズン1'

  const progress = calculateProgress(currentEpisode, totalEpisodes)
  const imageUrl = metadata?.imageUrl
  const nextEpText = formatNextEpisode(nextEpisodeDate ? new Date(nextEpisodeDate) : null)
  const airingNow = isAiringNow(nextEpisodeDate ? new Date(nextEpisodeDate) : null)

  const handleIncrement = (e) => {
    e.stopPropagation()
    setIsAnimating(true)
    onIncrement?.(anime.id)
    setTimeout(() => setIsAnimating(false), 300)
  }

  const handleOpenUrl = (e) => {
    e.stopPropagation()
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
    onOpenUrl?.(anime.id)
  }

  const handleDrop = (e) => {
    e.stopPropagation()
    onDrop?.(anime.id)
  }

  const handleSkip = (e) => {
    e.stopPropagation()
    onSkip?.(anime.id)
  }

  return (
    <div
      className={`anime-card ${isLongPressing ? 'long-pressing' : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onEdit?.(anime)}
      onMouseDown={handlePressStart}
      onMouseUp={handlePressEnd}
      onMouseLeave={handlePressCancel}
      onTouchStart={handlePressStart}
      onTouchEnd={handlePressEnd}
      onTouchCancel={handlePressCancel}
    >
      <div className="anime-card-header">
        {/* Thumbnail */}
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="anime-card-thumbnail"
            loading="lazy"
          />
        ) : (
          <div className="anime-card-thumbnail" />
        )}

        {/* Info */}
        <div className="anime-card-info">
          {/* プラットフォーム名（タイトル上） */}
          {platform && (
            <div className="anime-card-platform" data-platform={platform}>
              {getPlatformName(platform)}
            </div>
          )}

          <h3
            className="anime-card-title"
            onClick={(e) => {
              e.stopPropagation()
              if (url) window.open(url, '_blank', 'noopener,noreferrer')
            }}
            style={{ cursor: url ? 'pointer' : 'default' }}
            title={url ? '視聴URLを開く' : ''}
          >
            {title}
          </h3>

          <div className="anime-card-meta">
            {/* Season / Episode Info */}
            <div className="anime-season-info">
              <div className="anime-episode-text">
                {season || 'シーズン1'}：{currentEpisode || 1}話
              </div>
              {metadata?.broadcast && (
                <div className="anime-schedule-text">
                  {formatSchedule(metadata.broadcast)}
                </div>
              )}
            </div>

            <div className="anime-badges">
            {/* Next Episode Badge */}
            {airingNow ? (
              <span className="next-episode-badge airing-now">
                <Play size={12} />
                放送中
              </span>
            ) : nextEpText ? (
              <span className="next-episode-badge">
                <Clock size={12} />
                次回: {nextEpText}
              </span>
            ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar - Only show if totalEpisodes is known */}
      {totalEpisodes > 0 ? (
        <div className="progress-container">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      ) : (
        /* Spacer when no progress bar */
        <div style={{ marginTop: '12px' }}></div>
      )}
    </div>
  )
}

export default AnimeCard
